import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MessageSquarePlus, MessagesSquare, Search, Users } from 'lucide-react';
import { Alert, Button, EmptyState, Input, SkeletonText, Tooltip, IconButton, cn } from '../../ui';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { chatErr, filterConversations, isChatStaffOnlyError, useChatConversations } from './chatApi';
import { ChatThread } from './ChatThread';
import { ConversationRow, NewChatDialog, NewGroupDialog } from './ChatParts';

/**
 * Team chat — `/chat` and `/chat/:id`.
 *
 * Two panes on a desktop (conversation list · open thread); one at a time below `lg`, with a back
 * button in the thread. Staff-only: the route is closed to farmer / retailer sessions and the server
 * answers CHAT_STAFF_ONLY besides.
 *
 * The list is polled every 15 s and on focus; the open thread polls itself (see ChatThread).
 */
export function ChatPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const wide = useMediaQuery('(min-width: 1024px)');
  const [search, setSearch] = useState('');
  const [newChat, setNewChat] = useState(false);
  const [newGroup, setNewGroup] = useState(false);

  // Polled every 15 s and on focus; also keeps the launcher's unread badge in step (see the hook).
  const listQ = useChatConversations();
  const rows = useMemo(() => listQ.data ?? [], [listQ.data]);

  const q = search.trim();
  const shown = filterConversations(rows, q);

  const current = id ? rows.find((c) => c.id === id) : undefined;
  const staffOnly = isChatStaffOnlyError(listQ.error);

  const showList = wide || !id;
  const showThread = wide || !!id;

  return (
    <div className="flex h-[calc(100dvh-11rem)] min-h-[440px] overflow-hidden rounded-card border border-line bg-surface shadow-soft sm:h-[calc(100dvh-12rem)]">
      {/* ───────── Conversation list ───────── */}
      {showList && (
        <aside className={cn('flex min-h-0 flex-col', wide ? 'w-[340px] shrink-0 border-r border-line' : 'w-full')}>
          <div className="flex items-center gap-2 px-4 pb-2 pt-3.5">
            <MessagesSquare className="size-5 text-brand-600" />
            <h1 className="flex-1 truncate type-section-title text-content">Team chat</h1>
            <Tooltip label="New chat">
              <IconButton aria-label="New chat" diameter={36} className="border border-line text-content/70 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 dark:hover:bg-brand-900/20" onClick={() => setNewChat(true)}>
                <MessageSquarePlus />
              </IconButton>
            </Tooltip>
            <Tooltip label="New group">
              <IconButton aria-label="New group" diameter={36} className="border border-line text-content/70 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 dark:hover:bg-brand-900/20" onClick={() => setNewGroup(true)}>
                <Users />
              </IconButton>
            </Tooltip>
          </div>
          <div className="px-4 pb-3">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search chats"
              prefix={<Search />}
              aria-label="Search chats"
            />
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto border-t border-line">
            {listQ.isLoading ? (
              <div className="p-4"><SkeletonText lines={8} /></div>
            ) : listQ.isError ? (
              <div className="p-4">
                <Alert
                  type={staffOnly ? 'info' : 'error'}
                  message={staffOnly ? 'Team chat is for Green Bay staff.' : chatErr(listQ.error, 'Could not load your chats')}
                  action={staffOnly ? undefined : <Button size="sm" onClick={() => listQ.refetch()}>Retry</Button>}
                />
              </div>
            ) : shown.length === 0 ? (
              <EmptyState
                compact
                icon={<MessagesSquare />}
                title={q ? `No chats match “${q}”` : 'No conversations yet'}
                description={q ? undefined : 'Start a chat with a colleague or create a group.'}
                action={q ? undefined : <Button variant="primary" icon={<MessageSquarePlus size={16} />} onClick={() => setNewChat(true)}>New chat</Button>}
              />
            ) : (
              shown.map((c) => (
                <ConversationRow key={c.id} c={c} active={c.id === id} onOpen={(cid) => navigate(`/chat/${cid}`)} />
              ))
            )}
          </div>
        </aside>
      )}

      {/* ───────── Thread ───────── */}
      {showThread && (
        <section className="flex min-h-0 min-w-0 flex-1 flex-col">
          {id ? (
            <ChatThread key={id} id={id} summary={current} onBack={wide ? undefined : () => navigate('/chat')} />
          ) : (
            <EmptyState
              className="flex-1"
              icon={<MessagesSquare />}
              title="Pick a conversation"
              description="Choose a chat on the left, or start a new one."
              action={
                <div className="flex flex-wrap justify-center gap-2">
                  <Button variant="primary" icon={<MessageSquarePlus size={16} />} onClick={() => setNewChat(true)}>New chat</Button>
                  <Button icon={<Users size={16} />} onClick={() => setNewGroup(true)}>New group</Button>
                </div>
              }
            />
          )}
        </section>
      )}

      <NewChatDialog open={newChat} onClose={() => setNewChat(false)} />
      <NewGroupDialog open={newGroup} onClose={() => setNewGroup(false)} />
    </div>
  );
}
